export type SsoBank<T> = {
  search: string;
  total: number | null;
  slots: Array<T | undefined>;
};

export type UsersPageSlice<T> = {
  localSlice: T[];
  ssoOffset: number;
  ssoLimit: number;
};

export const sliceUsersPage = <T>(
  localRows: readonly T[],
  first: number,
  rows: number,
): UsersPageSlice<T> => {
  const start = Math.max(0, first);
  const size = Math.max(0, rows);
  const localSlice = localRows.slice(start, start + size);
  return {
    localSlice,
    ssoOffset: Math.max(0, start - localRows.length),
    ssoLimit: size - localSlice.length,
  };
};

export const createSsoBank = <T>(): SsoBank<T> => ({
  search: "",
  total: null,
  slots: [],
});

export const clearSsoBank = <T>(bank: SsoBank<T>): void => {
  bank.search = "";
  bank.total = null;
  bank.slots = [];
};

export const fillSsoWindow = <T>(
  bank: SsoBank<T>,
  search: string,
  offset: number,
  rows: readonly T[],
  total: number,
): void => {
  if (bank.search !== search) {
    bank.search = search;
    bank.slots = [];
  }
  bank.total = total;
  rows.forEach((row, index) => {
    bank.slots[offset + index] = row;
  });
};

export const hoistOwner = <T extends { email: string }>(
  rows: readonly T[],
  email: string,
): T[] => {
  if (!email) {
    return [...rows];
  }
  const wanted = email.toLowerCase();
  const current = rows.find((row) => row.email.toLowerCase() === wanted);
  if (!current) {
    return [...rows];
  }
  return [current, ...rows.filter((row) => row.email.toLowerCase() !== wanted)];
};

export type VisibleSsoFill<T> =
  | { status: "ready"; rows: T[] }
  | { status: "fetch"; offset: number };

export const contiguousSsoRows = <T>(bank: SsoBank<T>, search: string): T[] => {
  if (bank.search !== search || bank.total === null) {
    return [];
  }

  const rows: T[] = [];
  for (let index = 0; index < bank.slots.length; index += 1) {
    const slot = bank.slots[index];
    if (slot === undefined) {
      break;
    }
    rows.push(slot);
  }
  return rows;
};

export const OWNERS_FETCH_MAX = 100;

export const ssoChunkRequest = ({
  loaded,
  neededOffset,
  rawTotal,
}: {
  loaded: number;
  neededOffset: number;
  rawTotal: number | null;
}): { offset: number } | null => {
  if (rawTotal !== null && loaded >= rawTotal) {
    return null;
  }
  if (neededOffset < loaded) {
    return null;
  }
  return {
    offset: loaded,
  };
};

export const ownersChunkKey = (
  accountNumber: number,
  search: string,
  offset: number,
): string => `${accountNumber}\0${search}\0${offset}`;

export const shouldApplyOwnersChunk = (
  requestedAccount: number,
  currentAccount: number | null | undefined,
): boolean => currentAccount === requestedAccount;

export const listedUserTotal = ({
  localCount,
  visibleSsoCount,
  rawLoaded,
  rawTotal,
}: {
  localCount: number;
  visibleSsoCount: number;
  rawLoaded: number;
  rawTotal: number | null;
}): number | null => {
  if (rawTotal === null) {
    return null;
  }
  if (rawLoaded >= rawTotal) {
    return localCount + visibleSsoCount;
  }
  return localCount + rawTotal - (rawLoaded - visibleSsoCount);
};

export const takeVisibleSsoRows = <T>({
  rawRows,
  rawTotal,
  offset,
  limit,
  isVisible,
}: {
  rawRows: readonly T[];
  rawTotal: number | null;
  offset: number;
  limit: number;
  isVisible: (row: T) => boolean;
}): VisibleSsoFill<T> => {
  const visible = rawRows.filter(isVisible);
  const end = offset + limit;
  const rawExhausted = rawTotal !== null && rawRows.length >= rawTotal;

  if (limit <= 0 || visible.length >= end || rawExhausted) {
    return {
      status: "ready",
      rows: visible.slice(offset, end),
    };
  }

  return {
    status: "fetch",
    offset: rawRows.length,
  };
};

export const isListedSsoOwner = (
  owner: {
    email: string;
    representsDomain?: string;
    representsOrganization?: string;
  },
  localEmails: ReadonlySet<string>,
): boolean => {
  if (owner.representsDomain || owner.representsOrganization) {
    return false;
  }
  return !localEmails.has(owner.email.toLowerCase());
};
